import React, { useEffect, useMemo, useRef, useState } from "react";
import { ComponentData } from "@companion-core/shared/app/Interfaces/API";
import { PictureListLayoutProps } from "@companion-core/shared/app/Interfaces/blocks";
import { Button } from "@companion-core/shared/app/Components/Button";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import { getSanitizedDescriptionAlt } from "@companion-core/shared/app/Utils/componentHelper";
import "@companion-core/web/src/Assets/Styles/scss/picturelist.scss";
import { useDynamicPageStore } from "@companion-core/shared/app/Store/dynamicPageStore";

export const PictureListLayout = ({
  title,
  subtitle,
  cards,
  button,
  blockSlug,
}: PictureListLayoutProps) => {
  const { blocksBySlug } = useDynamicPageStore();
  const colorIndex = useMemo(() => {
    return blocksBySlug[blockSlug ?? ""]?.isLoaded
      ? (blocksBySlug[blockSlug ?? ""].colorIndex ?? 0)
      : 0;
  }, [blocksBySlug, blockSlug]);
  const ref = useRef<HTMLDivElement>(null);
  const [shouldShowArrows, setShouldShowArrows] = useState(false);

  useEffect(() => {
    const updateWidths = () => {
      if (ref.current) {
        const containerWidth = ref.current.offsetWidth;

        const images = ref.current.querySelectorAll(".picture-list-layout__image-container");
        const totalImageWidth = Array.from(images).reduce((total, img) => {
          const style = window.getComputedStyle(img);
          const marginLeft = parseInt(style.marginLeft, 10);
          const marginRight = parseInt(style.marginRight, 10);
          return total + (img as HTMLElement).offsetWidth + marginLeft + marginRight;
        }, 0);
        setShouldShowArrows(totalImageWidth > containerWidth);
      }
    };

    window.addEventListener("resize", updateWidths);
    updateWidths();

    return () => window.removeEventListener("resize", updateWidths);
  }, []);

  const handleArrowClick = (direction: "left" | "right") => {
    if (ref.current) {
      const imageWidth = 150;
      const imageMargin = 60;
      const scrollAmount =
        direction === "left" ? -(imageWidth + imageMargin) : imageWidth + imageMargin;
      ref.current.scrollLeft += scrollAmount;
    }
  };

  return (
    cards && (
      <section
        className={`picture-list-layout ${colorIndex % 2 === 0 ? "colored-background--odd" : "colored-background--even"}`}>
        <Title title={title} />
        <Subtitle subtitle={subtitle} />

        <div className="picture-list-layout__gallery">
          {shouldShowArrows && (
            <>
              <Arrow direction="left" onClick={() => handleArrowClick("left")} />
              <Arrow direction="right" onClick={() => handleArrowClick("right")} />
            </>
          )}

          <div ref={ref} className="picture-list-layout__slide">
            {cards.map((slide) => (
              <PictureCard key={`slide-${slide.id}`} card={slide} />
            ))}
          </div>
        </div>
        <GalleryButton button={button} />
      </section>
    )
  );
};

const GalleryButton = ({ button }: { button: ComponentData | null | undefined }) => {
  if (!button || !button.title) {
    return null;
  }
  return (
    <Button
      testID="block-picturelist-button"
      label={button.title?.toUpperCase()}
      classNames={["picture-list-layout__button"]}
    />
  );
};

const Title = ({ title }: { title: string | null }) => {
  if (!title) {
    return null;
  }
  return <h2 className="picture-list-layout__title super-title-3">{title}</h2>;
};

const Subtitle = ({ subtitle }: { subtitle: string | null | undefined }) => {
  if (!subtitle) {
    return null;
  }
  return <span className="picture-list-layout__subtitle title-8">{subtitle}</span>;
};

const PictureCard = ({ card }: { card: ComponentData }) => {
  const imageUrl = getCloudinaryOptimizedUrl(card.medias?.[0]?.url ?? "", {
    contentType: "card",
    width: window.innerWidth,
  });

  return (
    <div className="picture-list-layout__image-container">
      <img
        className="picture-list-layout__image"
        src={imageUrl}
        alt={getSanitizedDescriptionAlt(card.medias?.[0]?.description_alt) ?? ""}
      />
    </div>
  );
};

const Arrow = ({ direction, onClick }: { direction: "left" | "right"; onClick: () => void }) => {
  return (
    <div className={`picture-list-layout__${direction}-arrow d-flex top-50`} onClick={onClick}>
      <FontIcon name={`JP-Arrow-${direction}-bold`} className="gallery__icon" />
    </div>
  );
};
